<script setup lang="ts">
import LegalLayout from "@/modules/legal/components/LegalLayout.vue";
</script>

<template>
  <LegalLayout
    title="Privacybeleid"
    lead="Klupz verwerkt alleen de gegevens die nodig zijn om vrijwilligerstaken van een vereniging te organiseren. Uw vereniging bepaalt welke gegevens zij in Klupz zet; Bytechnology beheert het platform en verwerkt de gegevens in opdracht van de vereniging."
    updated="8 oktober 2026"
  >
    <h2>1. Wie is verantwoordelijk?</h2>
    <p>
      Voor de gegevens van leden en vrijwilligers is uw vereniging de
      verwerkingsverantwoordelijke. Bytechnology (eenmanszaak van Wilhelmus van
      Dongen, Maria van Loontoren 30, 4811 BR Breda, KvK 97949256,
      info@bytechnology.nl) is verwerker.
    </p>
    <p>
      Voor haar eigen beheer van het platform, zoals beveiligingslogs,
      supportverzoeken en facturatie aan verenigingen, is Bytechnology zelf
      verantwoordelijk. Wilt u uw rechten uitoefenen, vraag het dan in principe
      bij uw vereniging; Bytechnology helpt de vereniging daarbij.
    </p>
    <h2>2. Welke gegevens verwerken we?</h2>
    <table>
      <tr>
        <th>Categorie</th>
        <th>Gegevens</th>
        <th>Bron</th>
      </tr>
      <tr>
        <td>Ledengegevens</td>
        <td>
          Voornaam, tussenvoegsel, achternaam, e-mailadres, geboortedatum,
          relatienummer, type lidmaatschap, teams
        </td>
        <td>Vereniging (invoer of import)</td>
      </tr>
      <tr>
        <td>Account</td>
        <td>
          Gebruikers-ID, e-mailadres, ingelogd-status, wachtwoord of
          inlogmethode in de inlogdienst
        </td>
        <td>Gebruiker, vereniging</td>
      </tr>
      <tr>
        <td>Vrijwilligerswerk</td>
        <td>
          Inschrijvingen op taken, periodes, uren, vrijstellingen, afkoop,
          categorieën
        </td>
        <td>Gebruiker, vereniging</td>
      </tr>
      <tr>
        <td>Berichten</td>
        <td>E-mails over aanmelding, wijziging en herinnering</td>
        <td>Systeem</td>
      </tr>
      <tr>
        <td>Apparaat (app)</td>
        <td>Taal- en themavoorkeur, sessietoken op het toestel</td>
        <td>Gebruiker</td>
      </tr>
      <tr>
        <td>Technisch</td>
        <td>IP-adres, tijdstip en foutmeldingen in serverlogs</td>
        <td>Systeem</td>
      </tr>
      <tr>
        <td>Verenigingsgegevens</td>
        <td>Naam, logo en kleuren van de vereniging</td>
        <td>Vereniging</td>
      </tr>
    </table>
    <p>
      Klupz vraagt niet om bijzondere persoonsgegevens (zoals gezondheid) en
      verzamelt geen locatie of contacten. Vult uw vereniging zulke gegevens
      toch in vrije tekstvelden in, dan is zij daar zelf verantwoordelijk voor.
    </p>
    <h2>3. Waarvoor en op welke grondslag?</h2>
    <p>
      De vereniging gebruikt de gegevens om vrijwilligerstaken te plannen, leden
      uit te nodigen en in te delen, uren en afkoop bij te houden en te
      communiceren over taken. De grondslag is in de regel de uitvoering van de
      lidmaatschapsovereenkomst of het gerechtvaardigd belang van de vereniging
      bij een goed georganiseerde club.
    </p>
    <p>
      Bytechnology gebruikt technische gegevens om Klupz te beveiligen en te
      laten werken (gerechtvaardigd belang) en administratiegegevens om haar
      wettelijke verplichtingen na te komen. Gegevens worden niet verkocht en
      niet gebruikt voor advertenties of profilering.
    </p>
    <h2>4. Minderjarigen</h2>
    <p>
      Veel verenigingen hebben jeugdleden. Klupz vraagt zelf geen toestemming
      aan ouders of voogden. De vereniging blijft als
      verwerkingsverantwoordelijke verantwoordelijk voor de grondslag waarop zij
      gegevens van jeugdleden in Klupz zet (in de regel de
      lidmaatschapsovereenkomst), voor het informeren van leden en ouders, en
      voor het juiste gebruik van accounts van minderjarigen. Gegevens van
      jeugdleden worden alleen gebruikt voor taakplanning en communicatie
      daarover.
    </p>
    <h2>5. Met wie delen we gegevens?</h2>
    <p>
      Alleen met partijen die Klupz mogelijk maken (sub-verwerkers), onder een
      verwerkersovereenkomst:
    </p>
    <table>
      <tr>
        <th>Partij</th>
        <th>Doel</th>
        <th>Locatie</th>
      </tr>
      <tr>
        <td>TransIP</td>
        <td>
          Hosting van applicatie, database en back-ups, en verzenden van
          e-mailnotificaties
        </td>
        <td>Nederland</td>
      </tr>
    </table>
    <p>
      Binnen de vereniging zien bestuurders en beheerders de gegevens van leden;
      leden zien in het portaal vooral hun eigen gegevens en de taken.
      Verenigingen kunnen elkaars gegevens niet zien: de gegevens van elke
      vereniging zijn in de backend van elkaar gescheiden. Overige verstrekking
      vindt alleen plaats op grond van een wettelijke verplichting.
    </p>
    <h2>6. Cookies en opslag op uw apparaat</h2>
    <p>
      De portals gebruiken alleen functionele cookies en lokale opslag voor
      inloggen en voorkeuren (bijvoorbeeld taal, thema, gekozen club). De app
      bewaart het sessietoken versleuteld in de beveiligde opslag van het
      toestel.
    </p>
    <p>
      Er zijn geen analyse-, advertentie- of trackingcookies of -SDK's; daarom
      is er geen cookiebanner nodig. De iOS-app verklaart in zijn
      privacymanifest dat hij niet trackt.
    </p>
    <h2>7. Hoe lang bewaren we gegevens?</h2>
    <p>
      De vereniging bepaalt hoe lang zij gegevens van leden en vrijwilligers in
      Klupz houdt en kan leden verwijderen of laten verlopen. Verwijderde
      gegevens verdwijnen uit back-ups binnen 90 dagen.
    </p>
    <p>
      Na het einde van de overeenkomst met Bytechnology krijgt de vereniging 30
      dagen om te exporteren; daarna verwijdert Bytechnology alle gegevens van
      de vereniging. Serverlogs worden maximaal 30 dagen bewaard;
      facturatiegegevens zeven jaar (fiscale bewaarplicht).
    </p>
    <h2>8. Beveiliging</h2>
    <p>
      Verbindingen zijn versleuteld (HTTPS). Toegang loopt via een centrale
      inlogdienst met rollen (lid, beheerder van een vereniging,
      platformbeheerder). De gegevens van verenigingen zijn in de backend van
      elkaar gescheiden, de database is niet publiek bereikbaar en er zijn
      back-ups en updates.
    </p>
    <p>
      Bij een datalek informeert Bytechnology de vereniging zonder onredelijke
      vertraging en uiterlijk binnen 48 uur nadat Bytechnology er kennis van
      heeft genomen (ook via een melding van een sub-verwerker), zodat zij kan
      voldoen aan haar meldplicht bij de Autoriteit Persoonsgegevens (72 uur).
    </p>
    <h2>9. Uw rechten</h2>
    <p>
      U kunt uw vereniging vragen om inzage, correctie, verwijdering, beperking
      van verwerking en overdracht van uw gegevens, en bezwaar maken tegen
      verwerking. Heeft u toestemming gegeven, dan kunt u die altijd intrekken.
      Verzoeken beantwoordt de vereniging binnen een maand.
    </p>
    <p>
      Mail naar info@bytechnology.nl als u niet weet bij welke vereniging u moet
      zijn of als een vereniging niet reageert.
    </p>
    <h2>10. Klachten</h2>
    <p>
      Komt u er met uw vereniging of Bytechnology niet uit, dan kunt u een
      klacht indienen bij de Autoriteit Persoonsgegevens
      (autoriteitpersoonsgegevens.nl).
    </p>
    <h2>11. Wijzigingen</h2>
    <p>
      Bytechnology kan dit beleid aanpassen, bijvoorbeeld bij nieuwe functies.
      De actuele versie staat op klupz.nl; belangrijke wijzigingen meldt zij
      vooraf aan verenigingen.
    </p>
  </LegalLayout>
</template>
